import React, { useState, useEffect, useRef } from 'react';
import { PenLine, FileText, UserCheck, Upload, Eraser, Save, Trash2, PenTool, ImagePlus } from 'lucide-react';
import ReactSignatureCanvas from 'react-signature-canvas';
import FormInput from '../../../../../Component/ui/FormInput.jsx';

const RemarksAndSignatures = ({ onPrevious, formData, setFormData, onSubmit, isSubmitting }) => {
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // initialize fields from parent formData if present
    setFormData(prev => ({
      ...prev,
      remarksAndSignatures: {
        supervisorRemarksNote: prev.remarksAndSignatures?.supervisorRemarksNote || '',
        driverSignature: prev.remarksAndSignatures?.driverSignature || '',
        managerSignature: prev.remarksAndSignatures?.managerSignature || '',
        supervisorSignature: prev.remarksAndSignatures?.supervisorSignature || '',
        driverSignatureImg: prev.remarksAndSignatures?.driverSignatureImg || '',
        managerSignatureImg: prev.remarksAndSignatures?.managerSignatureImg || '',
        supervisorSignatureImg: prev.remarksAndSignatures?.supervisorSignatureImg || '',
        driverProblems: prev.remarksAndSignatures?.driverProblems || ''
      }
    }));
  }, []);

  const handleChange = (name, value) => {
    setFormData(prev => ({ 
      ...prev, 
      remarksAndSignatures: {
        ...prev.remarksAndSignatures,
        [name]: value
      }
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleReset = () => {
    setFormData(prev => ({
      ...prev,
      remarksAndSignatures: {
        supervisorRemarksNote: '',
        driverSignature: '',
        managerSignature: '',
        supervisorSignature: '',
        driverSignatureImg: '',
        managerSignatureImg: '',
        supervisorSignatureImg: '',
        driverProblems: ''
      }
    }));
    setErrors({});
    // clear canvas pads if present
    if (driverSigRef && driverSigRef.current) driverSigRef.current.clear && driverSigRef.current.clear();
    if (managerSigRef && managerSigRef.current) managerSigRef.current.clear && managerSigRef.current.clear();
    if (supervisorSigRef && supervisorSigRef.current) supervisorSigRef.current.clear && supervisorSigRef.current.clear();
  }; 

  const handleSubmit = async () => {
    const newErrors = {};

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    if (onSubmit) await onSubmit();
  };

  // signature refs & helpers
  const driverSigRef = useRef(null);
  const managerSigRef = useRef(null);
  const supervisorSigRef = useRef(null);

  const [showDriverPad, setShowDriverPad] = useState(false);
  const [showManagerPad, setShowManagerPad] = useState(false);
  const [showSupervisorPad, setShowSupervisorPad] = useState(false);

  const saveSignature = (name, ref) => {
    if (!ref.current || ref.current.isEmpty()) return;

    let dataUrl = '';
    try {
      // prefer trimmed canvas when available
      if (typeof ref.current.getTrimmedCanvas === 'function') {
        dataUrl = ref.current.getTrimmedCanvas().toDataURL('image/png');
      } else if (typeof ref.current.getCanvas === 'function') {
        dataUrl = ref.current.getCanvas().toDataURL('image/png');
      } else if (typeof ref.current.toDataURL === 'function') {
        dataUrl = ref.current.toDataURL();
      } else {
        // try known internals as a last resort
        const canvas = ref.current._canvas || ref.current.canvas;
        if (canvas && typeof canvas.toDataURL === 'function') dataUrl = canvas.toDataURL('image/png');
      }
    } catch (err) {
      // If trim-canvas import failed inside library, fallback to full canvas
      // log to help debugging in development
      // eslint-disable-next-line no-console
      console.warn('Signature trim failed, falling back to full canvas:', err);
      try {
        dataUrl = ref.current.getCanvas().toDataURL('image/png');
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to get signature canvas data URL', e);
        return;
      }
    }

    if (!dataUrl) return;

    setFormData(prev => ({ 
      ...prev, 
      remarksAndSignatures: {
        ...prev.remarksAndSignatures,
        [name]: dataUrl
      }
    }));
    if (name === 'driverSignatureImg') setShowDriverPad(false);
    if (name === 'managerSignatureImg') setShowManagerPad(false);
    if (name === 'supervisorSignatureImg') setShowSupervisorPad(false);
  };

  const clearSignaturePad = (ref) => {
    if (ref.current) ref.current.clear();
  };

  // hidden file input refs for signature uploads
  const driverFileRef = useRef(null);
  const managerFileRef = useRef(null);
  const supervisorFileRef = useRef(null);

  const fileRefMap = {
    driverSignatureImg: driverFileRef,
    managerSignatureImg: managerFileRef,
    supervisorSignatureImg: supervisorFileRef,
  };

  // Reusable signature card renderer
  const renderSignatureCard = (label, nameField, imgField, sigRef, showPad, setShowPad, color) => {
    const colorMap = {
      blue:   { bg: 'bg-blue-50',  border: 'border-blue-200', badge: 'bg-blue-100 text-blue-700', accent: 'text-blue-600' },
      purple: { bg: 'bg-purple-50', border: 'border-purple-200', badge: 'bg-purple-100 text-purple-700', accent: 'text-purple-600' },
      amber:  { bg: 'bg-amber-50',  border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700', accent: 'text-amber-600' },
    };
    const c = colorMap[color] || colorMap.blue;
    const hasImg = !!formData.remarksAndSignatures?.[imgField];

    const fileRef = fileRefMap[imgField];

    return (
      <div className={`rounded-xl border ${c.border} ${c.bg} p-3 sm:p-4 space-y-3 overflow-hidden min-w-0 max-w-full box-border`}>
        {/* Card Header */}
        <div className="flex items-center gap-2 min-w-0">
          <span className={`w-7 h-7 flex-shrink-0 rounded-full flex items-center justify-center ${c.badge}`}>
            <UserCheck size={14} />
          </span>
          <span className="text-sm font-semibold text-gray-800 truncate">{label}</span>
          {hasImg && (
            <span className="ml-auto flex-shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200">
              Signed
            </span>
          )}
        </div>

        {/* Name Input */}
        <input
          type="text"
          value={formData.remarksAndSignatures?.[nameField] || ''}
          onChange={(e) => handleChange(nameField, e.target.value)}
          placeholder={`Type ${label.toLowerCase()} name`}
          className="w-full text-sm px-3 py-2.5 rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 placeholder-gray-400 transition"
        />

        {/* Saved Signature Preview */}
        {hasImg && (
          <div className="relative inline-block">
            <img
              src={formData.remarksAndSignatures[imgField]}
              alt={`${label} signature`}
              className="h-16 object-contain rounded-lg border border-gray-200 bg-white p-1"
            />
            <button
              type="button"
              onClick={() => {
                setFormData(prev => ({ ...prev, remarksAndSignatures: { ...prev.remarksAndSignatures, [imgField]: '' } }));
                clearSignaturePad(sigRef);
              }}
              className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 shadow-md transition-transform hover:scale-110"
              title="Remove signature"
            >
              <Trash2 size={12} />
            </button>
          </div>
        )}

        {/* Hidden file input - placed outside flex to avoid layout interference */}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          style={{ display: 'none', position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}
          onChange={(e) => {
            if (e.target.files?.[0]) {
              handleChange(imgField, URL.createObjectURL(e.target.files[0]));
            }
            e.target.value = '';
          }}
        />

        {/* Optional hint */}
        <p className="text-[11px] text-gray-400 italic">Sign or upload a selfie — optional</p>

        {/* Action Buttons */}
        <div className="flex gap-2 w-full min-w-0">
          <button
            type="button"
            onClick={() => setShowPad(prev => !prev)}
            className={`flex-1 min-w-0 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-xs font-semibold transition ${
              showPad
                ? 'bg-gray-800 text-white'
                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <PenTool size={13} className="flex-shrink-0" />
            <span className="truncate">{showPad ? 'Close' : 'Sign'}</span>
          </button>

          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="flex-1 min-w-0 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-xs font-semibold bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 transition"
          >
            <ImagePlus size={13} className="flex-shrink-0" />
            <span className="truncate">Upload</span>
          </button>
        </div>

        {/* Signature Pad */}
        {showPad && (
          <div className="rounded-xl border-2 border-dashed border-gray-300 bg-white overflow-hidden" style={{ maxWidth: '100%' }}>
            <ReactSignatureCanvas
              ref={sigRef}
              penColor="#1e293b"
              canvasProps={{ style: { width: '100%', height: 130, touchAction: 'none' } }}
            />
            <div className="flex gap-2 p-2 bg-gray-50 border-t border-gray-200">
              <button
                type="button"
                onClick={() => clearSignaturePad(sigRef)}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-white border border-gray-300 text-gray-600 hover:bg-gray-100 transition"
              >
                <Eraser size={13} /> Clear
              </button>
              <button
                type="button"
                onClick={() => saveSignature(imgField, sigRef)}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 shadow-sm transition"
              >
                <Save size={13} /> Save
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-5 pb-24 overflow-x-hidden">
      {/* Heading */}
      <div className="pb-3 pl-2 mb-2 border-b border-gray-200 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          <PenLine size={20} className="text-blue-600" />
          Remarks & Signatures
        </h3>
      </div>

      {/* Supervisor Remarks */}
      <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-3 sm:p-4 space-y-2">
        <label className="flex items-center gap-2 text-sm font-semibold text-gray-800">
          <FileText size={16} className="text-blue-500 flex-shrink-0" />
          Supervisor Remarks Note
        </label>
        <textarea
          value={formData.remarksAndSignatures?.supervisorRemarksNote || ''}
          onChange={(e) => handleChange('supervisorRemarksNote', e.target.value)}
          placeholder="Enter supervisor's remarks here..."
          rows={3}
          className="w-full text-sm px-3 py-2.5 rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 placeholder-gray-400 transition resize-none"
        />
      </div>

      {/* Driver Problems */}
      <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-3 sm:p-4 space-y-2">
        <label className="flex items-center gap-2 text-sm font-semibold text-gray-800">
          <FileText size={16} className="text-blue-500 flex-shrink-0" />
          Driver Problems
        </label>
        <textarea
          value={formData.remarksAndSignatures?.driverProblems || ''}
          onChange={(e) => handleChange('driverProblems', e.target.value)}
          placeholder="Describe any driver problems..."
          rows={2}
          className="w-full text-sm px-3 py-2.5 rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 placeholder-gray-400 transition resize-none"
        />
      </div>

      {/* Section Divider */}
      <div className="flex items-center gap-3 px-1">
        <div className="flex-1 h-px bg-gray-200" />
        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Signatures</span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>

      {/* Signature Cards */}
      <div className="space-y-4 min-w-0">
        {renderSignatureCard('Driver', 'driverSignature', 'driverSignatureImg', driverSigRef, showDriverPad, setShowDriverPad, 'blue')}
        {renderSignatureCard('Manager', 'managerSignature', 'managerSignatureImg', managerSigRef, showManagerPad, setShowManagerPad, 'purple')}
        {renderSignatureCard('Supervisor', 'supervisorSignature', 'supervisorSignatureImg', supervisorSigRef, showSupervisorPad, setShowSupervisorPad, 'amber')}
      </div>

      {/* Bottom Buttons */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 flex gap-3 md:relative md:border-t-0 md:mt-8 md:gap-4">
        <button
          onClick={onPrevious}
          className="flex-1 px-6 py-3 border-2 border-gray-400 text-gray-700 rounded-lg hover:bg-gray-100 transition font-semibold text-sm md:text-base"
        >
          Previous
        </button>

        <button
          onClick={handleReset}
          className="flex-1 px-6 py-3 border-2 border-gray-400 text-gray-700 rounded-lg hover:bg-gray-100 transition font-semibold text-sm md:text-base"
        >
          Reset
        </button>

        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition font-semibold text-sm md:text-base shadow-md"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Submitting...
            </span>
          ) : 'Submit'}
        </button>
      </div>
    </div>
  );
};

export default RemarksAndSignatures;
